import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import QuestionModal from './QuestionModal';
import StatusBar from './StatusBar';
import { useAuth } from './contexts/AuthContext';
import Auth from './components/Auth';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import UrlInput from './components/UrlInput'; // Import the new UrlInput component

// API endpoint configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function extractPlaylistId(url) {
  const match = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function getPlaylistIdFromPath() {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname || '';
  const trimmed = path.replace(/^\/+/, '');
  if (!trimmed) return null;
  const valid = trimmed.match(/^[a-zA-Z0-9_-]+$/);
  return valid ? trimmed : null;
}

function App() {
  return <MainApp />;
}

function MainApp() {
  const { currentUser } = useAuth();
  const [playlistUrl, setPlaylistUrl] = useState(() => localStorage.getItem('playlistUrl') || '');
  const [sourceType, setSourceType] = useState('');
  const [urlHistory, setUrlHistory] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summarizing, setSummarizing] = useState(new Set());
  const [summarizingMode, setSummarizingMode] = useState({});
  const [ttsLoading, setTtsLoading] = useState(new Set());
  const [operationStartTimes, setOperationStartTimes] = useState({});
  const [now, setNow] = useState(Date.now());
  const [filterInstructions, setFilterInstructions] = useState(() => localStorage.getItem('filterInstructions') || '');
  const [selectedVoice, setSelectedVoice] = useState(() => localStorage.getItem('selectedVoice') || '');
  const [allVoices, setAllVoices] = useState([]);
  const [audioUrls, setAudioUrls] = useState({});
  const [ttsSpeed, setTtsSpeed] = useState(() => Number(localStorage.getItem('ttsSpeed')) || 1.0);
  const audioRefs = useRef({}); // { [videoId]: ref }
  const [questionModal, setQuestionModal] = useState({ open: false, videoId: null });
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('summary'); // 'summary' or 'comments'
  const [voiceLanguageFilter, setVoiceLanguageFilter] = useState(() => {
    const stored = localStorage.getItem('voiceLanguageFilter') || 'all';
    return stored.includes('-') ? stored.split('-')[0] : stored; // migrate to base language code
  });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('default');
  const [balance, setBalance] = useState(null);
  const [initialBalance, setInitialBalance] = useState(null);
  const [isUrlInputFocused, setIsUrlInputFocused] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const setAppHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    
    window.addEventListener('resize', setAppHeight);
    setAppHeight();
  
    return () => {
      window.removeEventListener('resize', setAppHeight);
    };
  }, []);

  const getAuthHeader = useCallback(async () => {
    if (!currentUser) {
      setAuthModalOpen(true);
      return null;
    }
    const token = await currentUser.getIdToken();
    return { 'Authorization': `Bearer ${token}` };
  }, [currentUser]);

  const authedFetch = async (url, options) => {
    const response = await fetch(url, options);
    if (response.status === 401) {
      setAuthModalOpen(true);
      throw new Error('Unauthorized');
    }
    return response;
  };

  const fetchBalance = useCallback(async () => {
    if (!currentUser) return;
    try {
      const authHeader = await getAuthHeader();
      if (!authHeader) return;
      const response = await authedFetch(`${API_BASE_URL}/api/balance`, { headers: authHeader });
      const data = await response.json();
      setBalance(data.balance);
      setInitialBalance(data.initialBalance);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  }, [currentUser, getAuthHeader]);

  useEffect(() => {
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000); // Refresh balance every 30 seconds
    return () => clearInterval(interval);
  }, [fetchBalance]);

  useEffect(() => {
    try {
      const storedHistory = localStorage.getItem('urlHistory');
      if (storedHistory) {
        setUrlHistory(JSON.parse(storedHistory));
      }
    } catch (error) {
      console.error('Failed to parse urlHistory from localStorage', error);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('urlHistory', JSON.stringify(urlHistory));
  }, [urlHistory]);

  useEffect(() => {
    localStorage.setItem('playlistUrl', playlistUrl);
  }, [playlistUrl]);

  useEffect(() => {
    localStorage.setItem('filterInstructions', filterInstructions);
  }, [filterInstructions]);

  useEffect(() => {
    localStorage.setItem('ttsSpeed', ttsSpeed);
  }, [ttsSpeed]);

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/voices`);
        const voices = await response.json();
        const googleVoices = voices.map(v => ({ ...v, id: `google:${v.value}:${v.lang}` }));
        setAllVoices(googleVoices);

        const hasSelected = googleVoices.some(v => v.id === selectedVoice);
        if (!hasSelected && googleVoices.length > 0) {
          const candidate = voiceLanguageFilter === 'all'
            ? googleVoices[0]
            : (googleVoices.find(v => (v.lang || '').split('-')[0] === voiceLanguageFilter) || googleVoices[0]);
          setSelectedVoice(candidate.id);
        }
      } catch (err) {
        setError('Failed to load voices. Please reload.');
      }
    };
    fetchVoices();
  }, [selectedVoice, voiceLanguageFilter, setSelectedVoice]);

  // Persist language filter
  useEffect(() => {
    localStorage.setItem('voiceLanguageFilter', voiceLanguageFilter);
  }, [voiceLanguageFilter]);


  // If the selected voice does not match the language filter, auto pick the first available
  useEffect(() => {
    if (!allVoices || allVoices.length === 0) return;
    if (voiceLanguageFilter === 'all') return;
    const selected = allVoices.find(v => v.id === selectedVoice);
    const selectedBase = selected?.lang ? selected.lang.split('-')[0] : null;
    if (!selected || selectedBase !== voiceLanguageFilter) {
      const firstMatching = allVoices.find(v => (v.lang || '').split('-')[0] === voiceLanguageFilter);
      if (firstMatching) setSelectedVoice(firstMatching.id);
    }
  }, [voiceLanguageFilter, allVoices, selectedVoice, setSelectedVoice]);

  useEffect(() => {
    Object.entries(audioRefs.current).forEach(([videoId, ref]) => {
      if (ref && ref.current) {
        ref.current.playbackRate = ttsSpeed;
      }
    });
  }, [ttsSpeed, audioUrls]);



  // If URL path includes a playlist id, auto load it
  useEffect(() => {
    const pid = getPlaylistIdFromPath();
    if (pid) {
      const url = `https://www.youtube.com/playlist?list=${pid}`;
      setPlaylistUrl(url);
      // fetchVideos(url); // This will be called by the user
    }
  }, []);

  // Handle back/forward navigation between playlist ids
  useEffect(() => {
    const onPop = () => {
      const pid = getPlaylistIdFromPath();
      if (pid) {
        const url = `https://www.youtube.com/playlist?list=${pid}`;
        setPlaylistUrl(url);
        // fetchVideos(url);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleVoiceChangeValue = (newVoiceId) => {
    setSelectedVoice(newVoiceId);
    localStorage.setItem('selectedVoice', newVoiceId);
  };

  const getLanguageNameFromTag = (tag) => {
    try {
      const base = tag.split('-')[0];
      const dn = new Intl.DisplayNames(['en'], { type: 'language' });
      const name = dn.of(base);
      return name || tag;
    } catch (e) {
      return tag;
    }
  };

  const fetchVideos = async (overrideUrl, overrideFilter) => {
    const requestId = uuidv4();
    setLoading(true);
    setError('');

    try {
      const usedUrl = overrideUrl || playlistUrl;
      const usedFilter = overrideFilter || filterInstructions;
      
      let headers = {};
      if (usedFilter.trim()) {
        const authHeader = await getAuthHeader();
        if (!authHeader) {
          setLoading(false); // Stop loading if auth is required but not provided
          return;
        }
        headers = authHeader;
      }

      const response = await authedFetch(`${API_BASE_URL}/playlist`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          youtube_url: usedUrl,
          filter_instructions: usedFilter,
          requestId,
          client: 'web',
        }),
      });
      const data = await response.json();

      if (data.status === 'success') {
        setVideos(data.videos);
        setSourceType(data.sourceType || '');

        setUrlHistory(prevHistory => {
          const newHistory = [...prevHistory];
          const existingIndex = newHistory.findIndex(item => item.url === usedUrl);
          const newEntry = { url: usedUrl, title: data.title, type: data.sourceType };

          if (existingIndex > -1) {
            newHistory.splice(existingIndex, 1);
          }
          newHistory.unshift(newEntry);
          return newHistory.slice(0, 10); // Keep history limited to 10 items
        });

        const pid = extractPlaylistId(usedUrl);
        if (pid) {
          const qs = window.location.search || '';
          window.history.replaceState(null, '', `/${pid}${qs}`);
        }
      } else {
        setError(data.message || 'An error occurred');
      }
    } catch (err) {
      setError('Failed to fetch videos. Make sure the server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!questionModal.videoId || !questionText.trim()) return;
    setQuestionModal({ open: false, videoId: null });
    if (questionType === 'comments') {
      fetchCommentsSummaryAndRead(questionModal.videoId, 'comments-question', questionText.trim());
    } else {
      fetchSummaryAndRead(questionModal.videoId, 'summary', questionText.trim());
    }
    setQuestionText('');
  };

  // Update fetchSummaryAndRead to accept a mode parameter and use it in the API call. Wire up the M, S, and clickbait buttons to call fetchSummaryAndRead with the appropriate mode.
  const fetchSummaryAndRead = async (videoId, mode, customPrompt) => {
    const requestId = uuidv4();
    setSummarizing(prev => new Set(prev).add(videoId));
    setSummarizingMode(prev => ({ ...prev, [videoId]: mode || 'long' }));
    setOperationStartTimes(prev => ({ ...prev, [videoId]: Date.now() }));
    setError('');
    try {
      const modeParam = mode ? `&mode=${encodeURIComponent(mode)}` : '';
      const customPromptParam = customPrompt ? `&custom_prompt=${encodeURIComponent(customPrompt)}` : '';
      const selected = allVoices.find(v => v.id === selectedVoice);
      const languageName = selected ? getLanguageNameFromTag(selected.lang) : 'English';
      const authHeader = await getAuthHeader();
      if (!authHeader) return;
      const response = await authedFetch(`${API_BASE_URL}/summarize?youtube_url=https://www.youtube.com/watch?v=${videoId}${modeParam}${customPromptParam}&language=${encodeURIComponent(languageName)}&requestId=${requestId}&client=web`, { headers: authHeader });
      const data = await response.json();
      if (data.summary_text) {
        const selected = allVoices.find(v => v.id === selectedVoice);
        if (selected) {
          try {
            setTtsLoading(prev => new Set(prev).add(videoId));
            // Use backend TTS
            const ttsAuthHeader = await getAuthHeader();
            if (!ttsAuthHeader) return;
            const ttsRes = await authedFetch(`${API_BASE_URL}/tts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...ttsAuthHeader },
              body: JSON.stringify({
                text: data.summary_text,
                lang: selected.lang,
                voice: selected.value,
                speed: 1,
                videoId,
                requestId,
                client: 'web'
              })
            });
            if (!ttsRes.ok) throw new Error('TTS synthesis failed');
            const blob = await ttsRes.blob();
            const url = URL.createObjectURL(blob);
            setAudioUrls(prev => ({ ...prev, [videoId]: url }));
          } finally {
            setTtsLoading(prev => {
              const newSet = new Set(prev);
              newSet.delete(videoId);
              return newSet;
            });
          }
        } else {
          setError('No valid voice selected.');
        }
      } else {
        setError(data.message || 'Failed to get summary.');
      }
    } catch (err) {
      setError('Failed to fetch summary.');
    } finally {
      setSummarizing(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoId);
        return newSet;
      });
      setSummarizingMode(prev => {
        const newModes = { ...prev };
        delete newModes[videoId];
        return newModes;
      });
      setOperationStartTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[videoId];
        return newTimes;
      });
    }
  };

  

  const fetchCommentsSummaryAndRead = async (videoId, mode = 'comments', customPromptOverride) => {
    const requestId = uuidv4();
    setSummarizing(prev => new Set(prev).add(videoId));
    setSummarizingMode(prev => ({ ...prev, [videoId]: mode }));
    setOperationStartTimes(prev => ({ ...prev, [videoId]: Date.now() }));
    setError('');
    try {
      const promptParam = customPromptOverride !== undefined
        ? `&custom_prompt=${encodeURIComponent(customPromptOverride)}`
        : '';
      const selected = allVoices.find(v => v.id === selectedVoice);
      const languageName = selected ? getLanguageNameFromTag(selected.lang) : 'English';
      const authHeader = await getAuthHeader();
      if (!authHeader) return;
      const response = await authedFetch(`${API_BASE_URL}/comments-summarize?youtube_url=https://www.youtube.com/watch?v=${videoId}${promptParam}&language=${encodeURIComponent(languageName)}&requestId=${requestId}&client=web`, { headers: authHeader });
      const data = await response.json();
      if (data.comments_summary_text) {
        const selected = allVoices.find(v => v.id === selectedVoice);
        if (selected) {
          try {
            setTtsLoading(prev => new Set(prev).add(videoId));
            // Use backend TTS
            const ttsAuthHeader = await getAuthHeader();
            if (!ttsAuthHeader) return;
            const ttsRes = await authedFetch(`${API_BASE_URL}/tts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...ttsAuthHeader },
              body: JSON.stringify({
                text: data.comments_summary_text,
                lang: selected.lang,
                voice: selected.value,
                speed: 1,
                videoId,
                requestId,
                client: 'web'
              })
            });
            if (!ttsRes.ok) throw new Error('TTS synthesis failed');
            const blob = await ttsRes.blob();
            const url = URL.createObjectURL(blob);
            setAudioUrls(prev => ({ ...prev, [videoId]: url }));
          } finally {
            setTtsLoading(prev => {
              const newSet = new Set(prev);
              newSet.delete(videoId);
              return newSet;
            });
          }
        } else {
          setError('No valid voice selected.');
        }
      } else {
        setError(data.message || 'Failed to get comments summary.');
      }
    } catch (err) {
      setError('Failed to fetch comments summary.');
    } finally {
      setSummarizing(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoId);
        return newSet;
      });
      setSummarizingMode(prev => {
        const newModes = { ...prev };
        delete newModes[videoId];
        return newModes;
      });
      setOperationStartTimes(prev => {
        const newTimes = { ...prev };
        delete newTimes[videoId];
        return newTimes;
      });
    }
  };

  const skipAudio = (videoId, seconds) => {
    const audioEl = audioRefs.current[videoId]?.current;
    if (audioEl) {
      audioEl.currentTime += seconds;
    }
  };

  const handleLogout = async () => {
    try {
      const authHeader = await getAuthHeader();
      if(authHeader) {
        await authedFetch(`${API_BASE_URL}/api/logout`, { method: 'POST', headers: authHeader });
      }
    } catch (err) {
      console.error('Failed to revoke token on backend', err);
    } finally {
      signOut(auth).catch(error => console.error('Logout failed', error));
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("Are you sure you want to permanently delete your account and all associated data? This cannot be undone.")) {
      return;
    }
    
    try {
      const authHeader = await getAuthHeader();
      if(authHeader) {
        const response = await authedFetch(`${API_BASE_URL}/api/user/account`, { method: 'DELETE', headers: authHeader });
        if (response.ok) {
          alert('Account completely deleted.');
        } else {
          const body = await response.json().catch(() => ({}));
          console.error("Deletion failed:", body);
          alert(`Failed to delete account: ${body.message || 'Unknown error'}`);
        }
      }
    } catch (err) {
      console.error('Failed to delete account on backend', err);
      alert('Network error while deleting account.');
    } finally {
      signOut(auth).catch(error => console.error('Logout failed', error));
    }
  };

  const sortedVideos = sortOrder === 'reversed' ? [...videos].reverse() : videos;

  return (
    <div className="App">
      <header className="App-header">
        <>
          {currentUser && (
            <div className="user-info">
              {balance !== null && initialBalance !== null && (
                <span className="balance-display">
                  Used: {((initialBalance - balance) * 10000).toFixed(0)} / {(initialBalance * 10000).toFixed(0)}
                </span>
              )}
              <button className="delete-account-btn" onClick={handleDeleteAccount} style={{ backgroundColor: '#ff4d4f', color: '#fff', marginLeft: '10px' }}>Delete Account</button>
              <button onClick={handleLogout} style={{ marginLeft: '10px' }}>Logout</button>
            </div>
          )}
          <h1>Transcribr</h1>
          <div className="controls-container">
            <UrlInput
              playlistUrl={playlistUrl}
              setPlaylistUrl={setPlaylistUrl}
              isUrlInputFocused={isUrlInputFocused}
              setIsUrlInputFocused={setIsUrlInputFocused}
              isHistoryOpen={isHistoryOpen}
              setIsHistoryOpen={setIsHistoryOpen}
              urlHistory={urlHistory}
              setUrlHistory={setUrlHistory}
              fetchVideos={fetchVideos}
              loading={loading}
              filterInstructions={filterInstructions}
            />
            <div className="input-container">
              {filterInstructions && (
                <button className="clear-btn-left" onClick={() => setFilterInstructions('')}>
                  &times;
                </button>
              )}
              <input
                type="text"
                value={filterInstructions}
                onChange={(e) => setFilterInstructions(e.target.value)}
                placeholder="Enter filter instructions (optional) - e.g., 'Only show AI videos'"
                style={{ borderRadius: filterInstructions ? '0 4px 4px 0' : '4px' }}
              />
            </div>
          </div>
        </>
      </header>
      <div className="video-section-header">
        <button
          className={`sort-toggle-button ${sortOrder === 'reversed' ? 'reversed' : ''}`}
          onClick={() => setSortOrder(prev => (prev === 'default' ? 'reversed' : 'default'))}
        >
          <span className="sort-icon">{sortOrder === 'default' ? '⇅' : '⇵'}</span>
        </button>
        {sourceType && videos.length > 0 && (
          <div className="source-type-title">
            {sourceType.charAt(0).toUpperCase() + sourceType.slice(1)} ({videos.length} Videos)
          </div>
        )}
      </div>
      <div className="video-list">
        {sortedVideos.map((video) => (
          <div key={video.videoId} className="video-item">
            <a href={`https://www.youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noopener noreferrer">
              <img src={video.thumbnailUrl} alt={video.title} />
            </a>
            <div className="video-details">
              <h3>
                <a href={`https://www.youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noopener noreferrer">
                  {video.title}
                </a>
              </h3>
              <p>{video.channelName}</p>
              {(video.duration || video.publishedAt || video.viewCount) && (
                <div className="video-duration">
                  {video.duration && <span>⏱️ {video.duration}</span>}
                  {video.publishedAt && (
                    <span className="video-date">{video.duration ? ' • ' : ''}📅 {String(video.publishedAt)}</span>
                  )}
                  {video.viewCount && (
                    <span className="video-views">{(video.duration || video.publishedAt) ? ' • ' : ''}👁️ {String(video.viewCount)}</span>
                  )}
                </div>
              )}
              {(summarizing.has(video.videoId) || ttsLoading.has(video.videoId)) && (
                <div className="loading-indicator">
                  <p>
                    {ttsLoading.has(video.videoId) ? 'Generating audio...' : 'Summarizing...'}
                    ({Math.round((now - (operationStartTimes[video.videoId] || now)) / 1000)}s)
                  </p>
                </div>
              )}
              {audioUrls[video.videoId] && !summarizing.has(video.videoId) && !ttsLoading.has(video.videoId) && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, width: '100%', gap: '2px' }}>
                  <button 
                    onClick={() => skipAudio(video.videoId, -10)}
                    style={{ padding: '4px', cursor: 'pointer', background: 'none', border: 'none', fontSize: '16px' }}
                    title="-10s"
                  >
                    ⏪
                  </button>
                  <audio
                    ref={el => {
                      if (!audioRefs.current[video.videoId]) {
                        audioRefs.current[video.videoId] = { current: el };
                      } else {
                        audioRefs.current[video.videoId].current = el;
                      }
                    }}
                    src={audioUrls[video.videoId]}
                    controls
                    style={{ flex: 1, height: '32px' }}
                  />
                  <button 
                    onClick={() => skipAudio(video.videoId, 10)}
                    style={{ padding: '4px', cursor: 'pointer', background: 'none', border: 'none', fontSize: '16px' }}
                    title="+10s"
                  >
                    ⏩
                  </button>
                </div>
              )}
              <div className="button-row">
                <button className="small-summarize-btn" onClick={() => fetchSummaryAndRead(video.videoId, 'short')} disabled={summarizing.has(video.videoId)}>
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'short' ? '...' : 'S'}
                </button>
                <button className="small-summarize-btn" onClick={() => fetchSummaryAndRead(video.videoId, 'medium')} disabled={summarizing.has(video.videoId)}>
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'medium' ? '...' : 'M'}
                </button>
                <button className="small-summarize-btn" onClick={() => fetchSummaryAndRead(video.videoId, undefined)} disabled={summarizing.has(video.videoId)}>
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'long' ? '...' : 'L'}
                </button>
                <button className="small-summarize-btn" onClick={() => fetchSummaryAndRead(video.videoId, 'clickbait')} disabled={summarizing.has(video.videoId)}>
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'clickbait' ? '...' : '🎯'}
                </button>
                <button
                  className="small-summarize-btn"
                  disabled={summarizing.has(video.videoId)}
                  onClick={() => { setQuestionModal({ open: true, videoId: video.videoId }); setQuestionType('summary'); }}
                >
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'summary' ? '...' : '?'}
                </button>
                <button className="small-summarize-btn" onClick={() => fetchCommentsSummaryAndRead(video.videoId, 'comments', undefined)} disabled={summarizing.has(video.videoId)}>
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'comments' ? '...' : '💬'}
                </button>
                <button
                  className="small-summarize-btn"
                  disabled={summarizing.has(video.videoId)}
                  onClick={() => {
                    setQuestionModal({ open: true, videoId: video.videoId });
                    setQuestionType('comments');
                  }}
                >
                  {summarizing.has(video.videoId) && summarizingMode[video.videoId] === 'comments-question' ? '...' : '💬?'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <StatusBar 
        videos={videos}
        loading={loading}
        error={error}
        summarizing={summarizing}
        allVoices={allVoices}
        selectedVoice={selectedVoice}
        onChangeVoice={handleVoiceChangeValue}
        ttsSpeed={ttsSpeed}
        onChangeSpeed={setTtsSpeed}
        voiceLanguageFilter={voiceLanguageFilter}
        onChangeVoiceLanguageFilter={setVoiceLanguageFilter}
        onReload={() => fetchVideos(playlistUrl, filterInstructions)}
      />
      <QuestionModal
        open={questionModal.open}
        value={questionText}
        onChange={setQuestionText}
        onCancel={() => { setQuestionModal({ open: false, videoId: null }); setQuestionText(''); }}
        onAsk={handleAskQuestion}
        disabled={!questionText.trim()}
      />
      <Auth open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </div>
  );
}

export default App;
